
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import VendorSelect from '@/components/VendorSelect';
import { Vendor } from '@/types';
import { calculateDiscountAnalysis } from '@/services/api';
import DiscountResults from './DiscountResults';

const discountFormSchema = z.object({
  vendorDiscount: z.string().optional(),
  nestingaleDiscount: z.string().optional(),
  minProfit: z.string().optional(),
}).refine((data) => {
  return data.vendorDiscount || data.nestingaleDiscount;
}, {
  message: "At least one discount field is required",
  path: ["vendorDiscount"],
});

type DiscountFormData = z.infer<typeof discountFormSchema>;

const DiscountConfigurationForm = () => {
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DiscountFormData>({
    resolver: zodResolver(discountFormSchema),
  });

  const onSubmit = async (data: DiscountFormData) => {
    if (!selectedVendor) {
      toast({
        title: "Error",
        description: "Please select a vendor",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        vendorDiscount: data.vendorDiscount ? [{
          discountType: "vendor",
          discountPerc: parseFloat(data.vendorDiscount)
        }] : [],
        nestingaleDiscount: data.nestingaleDiscount ? [{
          discountType: "nestingale",
          discountPerc: parseFloat(data.nestingaleDiscount)
        }] : [],
        vendor: selectedVendor.name,
        inputAnalysis: "true",
        minProfit: data.minProfit ? parseFloat(data.minProfit) : undefined
      };

      const results = await calculateDiscountAnalysis(JSON.stringify(payload));
      setAnalysisResults(results);
      
      toast({
        title: "Success",
        description: "Discount analysis calculated successfully",
      });
    } catch (error) {
      console.error('Error calculating discount analysis:', error);
      toast({
        title: "Error",
        description: "Failed to calculate discount analysis",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorSelect = (vendor: Vendor | null) => {
    setSelectedVendor(vendor);
    setAnalysisResults(null);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <VendorSelect 
              onVendorSelect={handleVendorSelect} 
              disabled={isLoading} 
            />
            
            <div className="space-y-2">
              <Label htmlFor="vendorDiscount">Vendor Discount (%)</Label>
              <Input
                id="vendorDiscount"
                type="number"
                step="0.01"
                placeholder="Enter vendor discount percentage"
                {...register('vendorDiscount')}
                disabled={isLoading}
              />
              {errors.vendorDiscount && (
                <p className="text-sm text-red-500">{errors.vendorDiscount.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nestingaleDiscount">Nestingale Discount (%)</Label>
              <Input
                id="nestingaleDiscount"
                type="number"
                step="0.01"
                placeholder="Enter Nestingale discount percentage"
                {...register('nestingaleDiscount')}
                disabled={isLoading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="minProfit">Min. Profit ($) <span className="text-gray-500">(Optional)</span></Label>
              <Input
                id="minProfit"
                type="number"
                step="0.01"
                placeholder="Enter minimum profit amount"
                {...register('minProfit')}
                disabled={isLoading}
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={!selectedVendor || isLoading}
              className="w-full"
            >
              {isLoading ? 'Calculating...' : 'Calculate'}
            </Button>
          </div>
          
          <div className="space-y-4">
            {analysisResults && (
              <DiscountResults 
                results={analysisResults} 
                vendorName={selectedVendor?.displayName || ''} 
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default DiscountConfigurationForm;
